import { type AppState, Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { Auth0Client, type User } from "@auth0/auth0-spa-js";
import * as Sentry from "@sentry/react";
import { ROUTES } from "@src/common/constants";
import config from "@src/config";
import { LoggerName, getLogger } from "@src/utils/logConfig";
import type { UtmParams } from "@src/utils/utm-tracker";
import { saveUtmParams } from "@src/utils/utm-tracker";
import type React from "react";
import { type PropsWithChildren, useEffect } from "react";
import { useNavigate } from "react-router-dom";
interface Auth0ProviderWithNavigateProps {
	children: React.ReactNode;
}
const logger = getLogger(LoggerName.AUTH0_PROVIDER);

function Auth0SessionSync({ children }: PropsWithChildren) {
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

	useEffect(() => {
		if (isLoading || isAuthenticated) {
			return;
		}

		let cancelled = false;
		void auth0Client
			.isAuthenticated()
			.then((hasClientSession) => {
				if (!cancelled && hasClientSession) {
					return getAccessTokenSilently();
				}
			})
			.catch((error) => {
				logger.debug("Failed to sync Auth0Provider session from auth0Client", {
					error,
				});
			});

		return () => {
			cancelled = true;
		};
	}, [isLoading, isAuthenticated, getAccessTokenSilently]);

	return <>{children}</>;
}

const isElectron = Boolean(
	typeof window !== "undefined" && window.electronAPI?.isElectron,
);

/**
 * This client allows us to use Auth0's SPA SDK directly.
 * It is used to fetch the access token outside of reacts normal flow
 * in our getAccessToken function. This is useful for use in the react query client.
 *
 * Actual login/redirect happens using the react integration.
 */
export const auth0Client = new Auth0Client({
	domain: config.auth.domain,
	clientId: config.auth.clientId,
	useRefreshTokens: true,
	useRefreshTokensFallback: !isElectron,
	cacheLocation: "localstorage",
	authorizationParams: {
		audience: config.auth.audience,
		redirect_uri: config.auth.redirectUri,
	},
});

export const Auth0ProviderWithNavigate = ({
	children,
}: PropsWithChildren<Auth0ProviderWithNavigateProps>): React.ReactElement | null => {
	const navigate = useNavigate();

	const domain = config.auth.domain;
	const clientId = config.auth.clientId;
	const redirectUri = config.auth.redirectUri;
	const audience = config.auth.audience;

	const onRedirectCallback = (appState?: AppState, _user?: User) => {
		// Save UTM parameters if they were passed through appState
		if (appState?.utmParams) {
			saveUtmParams(appState.utmParams as UtmParams);
		}

		const returnTo =
			appState?.returnTo === "/index.html"
				? ROUTES.ask
				: appState?.returnTo || ROUTES.ask;
		navigate(returnTo, { replace: true });
	};

	if (!(domain && clientId && redirectUri && audience)) {
		const message =
			"Some of the auth information is missing in Auth0ProviderWithNavigate";

		logger.warn(message, {
			domain,
			clientId,
			redirectUri,
			audience,
		});

		Sentry.captureMessage(message, {
			level: "error",
			extra: {
				domain,
				clientId,
				redirectUri,
				audience,
			},
		});

		return null;
	}

	return (
		<Auth0Provider
			domain={domain}
			clientId={clientId}
			// enable refresh tokens
			useRefreshTokens={true}
			/**
			 * https://community.auth0.com/t/auth0-spa-2-x-returning-missing-refresh-token/98999/18
			 * https://stackoverflow.com/a/63620271
			 *
			 * The advice given in this thread is to enable useRefreshTokensFallback. The reasons cited are that it's more secure.
			 * Because token are stored in memory and the refresh token is stored in a third party iframe cookie, it's not possible to access
			 * the main access token from the iframe in the case of an XSS attack.
			 *
			 * The problem is that Safari does not allow third party cookies and Chrome is displaying warnings in the console. Saying
			 * they might be blocked in the future.
			 *
			 * The solution is to use the cacheLocation option to store the tokens in local storage. If the refresh token is missing or expired
			 * the user will need to login again.
			 */
			useRefreshTokensFallback={!isElectron}
			cacheLocation="localstorage"
			authorizationParams={{
				audience: audience,
				redirect_uri: redirectUri,
			}}
			onRedirectCallback={onRedirectCallback}
		>
			<Auth0SessionSync>{children}</Auth0SessionSync>
		</Auth0Provider>
	);
};

Auth0ProviderWithNavigate.displayName = "Auth0ProviderWithNavigate";
